import { Component, Host, State, getAssetPath, Element, h } from '@stencil/core';
import { Brand } from '../../models/stores/Brand';
import { brandService } from '../../utils/services/brandService';
import { translationService } from '../../utils/services/translationService';
// import { translationService } from '../../utils/services/translationService';

@Component({
  tag: 'aico-store-locator-map-ledger',
  styleUrl: 'aico-store-locator-map-ledger.css',
  shadow: true,
})
export class AicoStoreLocatorMapLedger {
  @Element() el!: HTMLElement;

  private brands: Brand[] = [];
  private locale: string = 'de_CH';
  private brandRefs: Map<number, HTMLElement> = new Map();
  private tooltipElement?: HTMLElement;
  private isMobile: boolean = false;
  private hideTooltipTimer?: number;

  @State() activeTooltipBrandId: number | null = null;

  componentWillLoad() {
    this.brands = brandService.getAllBrandsOrderedByPriority();
    // this.locale = translationService.getLocale();
    this.isMobile = window.matchMedia('(any-pointer: coarse)').matches;
    this.locale = translationService.getLocale();
  }

  componentDidLoad() {
    this.tooltipElement = document.createElement('div');
    this.tooltipElement.className = 'aico-brand-tooltip';
    this.tooltipElement.style.cssText = `
      position: absolute;
      background: rgb(17, 24, 39);
      color: white;
      padding: 16px;
      border-radius: 12px;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);
      border: 1px solid rgb(75, 85, 99);
      min-width: 200px;
      max-width: 450px;
      word-wrap: break-word;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
      pointer-events: none;
      z-index: 10000;
    `;
    this.el.shadowRoot!.appendChild(this.tooltipElement);

    document.addEventListener('click', this.handleDocumentClick, true);
  }

  disconnectedCallback() {
    // Check if the tooltip exists and is a child of the shadow root before removing
    if (this.tooltipElement && this.el.shadowRoot!.contains(this.tooltipElement)) {
        this.el.shadowRoot!.removeChild(this.tooltipElement);
    }
    document.removeEventListener('click', this.handleDocumentClick, true);
    clearTimeout(this.hideTooltipTimer);
  }

  // --- Event Handlers ---

  private handleBrandInteraction = (brand: Brand, event: MouseEvent) => {
    event.stopPropagation();
    if (this.isMobile) {
      if (this.activeTooltipBrandId === brand.brandId) {
        this.hideTooltip();
      } else {
        this.showTooltip(brand, event.currentTarget as HTMLElement);
      }
    } else {
      this.showTooltip(brand, event.currentTarget as HTMLElement);
    }
  };

  private handleMouseEnter = (brand: Brand, event: MouseEvent) => {
    if (this.isMobile) return;
    clearTimeout(this.hideTooltipTimer);
    this.showTooltip(brand, event.currentTarget as HTMLElement);
  };

  private handleMouseLeave = () => {
    if (this.isMobile) return;
    this.hideTooltipTimer = window.setTimeout(() => {
      this.hideTooltip();
    }, 300);
  };

  private handleDocumentClick = (event: Event) => {
    if (this.activeTooltipBrandId === null) return;

    const target = event.target as HTMLElement;
    const activeBrandRef = this.brandRefs.get(this.activeTooltipBrandId);

    if (activeBrandRef && !activeBrandRef.contains(target)) {
      this.hideTooltip();
    }
  };

  // --- Tooltip Logic ---

  private showTooltip = (brand: Brand, targetElement: HTMLElement) => {
    const description = brand.translations?.[this.locale]?.description;
    if (!description || !this.tooltipElement) return;

    this.activeTooltipBrandId = brand.brandId;

    this.tooltipElement.innerHTML = `
      <div style="font-weight: bold; text-align: center; margin-bottom: 8px;">
        ${brand.translations?.[this.locale].name}
      </div>
      <div>${description}</div>
      <div style="
        position: absolute;
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-top: 8px solid rgb(17, 24, 39);
      "></div>
    `;

    // --- Positioning Logic ---
    this.positionTooltip(this.tooltipElement, targetElement);
    this.tooltipElement.style.opacity = '1';
    this.tooltipElement.style.visibility = 'visible';
  };

  private positionTooltip(tooltip: HTMLElement, target: HTMLElement, margin: number = 16) {
    // Since the tooltip is now absolute to the host, we use offsetLeft and offsetTop
    // for positioning relative to the host.
    const hostRect = this.el.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    // Calculate position relative to the viewport first
    let top = targetRect.top - tooltip.offsetHeight - margin / 2;
    let left = targetRect.left + (targetRect.width / 2) - (tooltip.offsetWidth / 2);

    // Adjust for viewport boundaries
    if (left < margin) {
      left = margin;
    }
    if (left + tooltip.offsetWidth > window.innerWidth - margin) {
      left = window.innerWidth - tooltip.offsetWidth - margin;
    }
    if (top < margin) {
      top = targetRect.bottom + margin / 2;
    }

    // Convert viewport-relative coordinates to be relative to the host element
    tooltip.style.left = `${left - hostRect.left}px`;
    tooltip.style.top = `${top - hostRect.top}px`;
  }


  private hideTooltip = () => {
    if (!this.tooltipElement) return;
    this.activeTooltipBrandId = null;
    this.tooltipElement.style.opacity = '0';
    this.tooltipElement.style.visibility = 'hidden';
  };

  render() {
    return (
      <Host class="bottom-0 left-0 absolute bg-black/80 shadow-lg backdrop-blur-sm p-3 w-full overflow-visible">
        {/* The 'relative' class on this container is important for positioning the absolute tooltip */}
        <div class="relative overflow-x-auto scrollbar-hide">
          <div class="flex items-center space-x-6 min-w-max">
            {this.brands.map(brand => (
              <div
                class="group relative flex items-center hover:bg-white/10 px-3 py-2 rounded transition-all duration-200 cursor-pointer select-none"
                key={brand.brandId}
                ref={el => { if(el) this.brandRefs.set(brand.brandId, el); }}
                onClick={(e) => this.handleBrandInteraction(brand, e)}
                onMouseEnter={(e) => this.handleMouseEnter(brand, e)}
                onMouseLeave={this.handleMouseLeave}
              >
                <img
                  src={getAssetPath(brand.pinUrl || '')}
                  alt={`${brand.translations?.[this.locale]?.name || 'Brand'} pin`}
                  class="drop-shadow-sm mr-2 w-6 h-8 object-contain"
                />
                <span class="font-medium text-white text-sm whitespace-nowrap">
                  {brand.translations?.[this.locale]?.name || ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      </Host>
    );
  }
}
